import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Skeleton,
  LinearProgress,
  Fade,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LinkIcon from '@mui/icons-material/Link';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CloseIcon from '@mui/icons-material/Close';
import PostAddIcon from '@mui/icons-material/PostAdd';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../utils/api';
import AnimatedLogo from '../components/common/AnimatedLogo';
import FeatureLockOverlay from '../components/FeatureLockOverlay';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/common/LoadingScreen';

const TrendFinderPage = () => {
  const [query, setQuery] = useState('');
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [loadingTime, setLoadingTime] = useState(0);
  const [savedSearches, setSavedSearches] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const navigate = useNavigate();

  // New state for subscription check
  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(true);
  const [isFeatureAllowed, setIsFeatureAllowed] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState(null);

  // Plan IDs from environment variables
  const PRO_PLAN_ID = process.env.REACT_APP_PADDLE_PRO_MONTHLY;
  const PRO_YEARLY_ID = process.env.REACT_APP_PADDLE_PRO_YEARLY;
  const BUSINESS_PLAN_ID = process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY;
  const BUSINESS_YEARLY_ID = process.env.REACT_APP_PADDLE_BUSINESS_YEARLY;

  // Loading messages that change based on time elapsed
  const loadingMessages = [
    { time: 0, message: "Searching the web for trends...", subMessage: "This may take a moment" },
    { time: 3, message: "Analyzing trending topics...", subMessage: "Finding the most relevant information" },
    { time: 7, message: "Gathering insights...", subMessage: "Almost there" },
    { time: 12, message: "Compiling results...", subMessage: "This is taking longer than expected" }
  ];
  
  // Get the appropriate loading message based on time elapsed
  const getLoadingMessage = (seconds) => {
    const messageObj = [...loadingMessages].reverse().find(msg => seconds >= msg.time);
    return messageObj || loadingMessages[0];
  };

  useEffect(() => {
    const checkSubscription = async () => {
      setSubscriptionStatusLoading(true);
      try {
        // Use the configured api instance and the correct full path
        const response = await api.get('/api/subscriptions/status'); 
        const subscription = response.data.subscription;
        
        if (subscription && subscription.status === 'active') {
          setCurrentPlanId(subscription.price_id);
          if (
            subscription.price_id === PRO_PLAN_ID || 
            subscription.price_id === PRO_YEARLY_ID || 
            subscription.price_id === BUSINESS_PLAN_ID || 
            subscription.price_id === BUSINESS_YEARLY_ID
          ) {
            setIsFeatureAllowed(true);
          } else {
            setIsFeatureAllowed(false);
          }
        } else {
          setIsFeatureAllowed(false);
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
        setIsFeatureAllowed(false);
      } finally {
        setSubscriptionStatusLoading(false);
      }
    };
    checkSubscription();
  }, [PRO_PLAN_ID, PRO_YEARLY_ID, BUSINESS_PLAN_ID, BUSINESS_YEARLY_ID]);

  // Load saved searches on component mount
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem('trendFinderState');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          
          // Set the saved searches
          if (parsedData.savedSearches) {
            setSavedSearches(parsedData.savedSearches);
          }
          
          // If there's a most recent search, load it
          if (parsedData.lastSearch && parsedData.lastResults) {
            setQuery(parsedData.lastSearch);
            setTrends(parsedData.lastResults);
          }
        }
      } catch (error) {
        console.error('Error loading saved trend finder data:', error);
      } finally {
        setInitialLoad(false);
      }
    };
    
    loadSavedData();
  }, []);
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (!initialLoad) {
      const stateToSave = {
        savedSearches,
        lastSearch: query,
        lastResults: trends
      };
      
      localStorage.setItem('trendFinderState', JSON.stringify(stateToSave));
    }
  }, [savedSearches, query, trends, initialLoad]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    if (!isFeatureAllowed) {
      setError('This feature is available for Pro/Business plans.');
      return;
    }

    setLoading(true);
    setError('');
    setTrends(null);
    setLoadingTime(0);
    
    // Start a timer to track loading time
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      setLoadingTime(elapsedSeconds);
    }, 1000);
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.post('/api/ai/trends', 
        { query: query.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setTrends(response.data.topics);
      
      // Save this search to history if it's not already there
      if (!savedSearches.some(item => item.query === query.trim())) {
        setSavedSearches(prev => [
          { 
            id: Date.now(), 
            query: query.trim(),
            results: response.data.topics,
            date: new Date().toISOString()
          },
          ...prev
        ].slice(0, 10)); // Keep only the 10 most recent searches
      } else {
        // Update the existing search with new results
        setSavedSearches(prev => 
          prev.map(item => 
            item.query === query.trim() 
              ? { ...item, results: response.data.topics, date: new Date().toISOString() }
              : item
          )
        );
      }
    } catch (err) {
      console.error('Trend search error:', err);
      setError('Failed to fetch trends. Please try again.');
    } finally {
      setLoading(false);
      clearInterval(timer);
    }
  };

  const handleOpenTrend = (trend) => {
    setSelectedTrend(trend);
  };
  
  const handleCloseTrend = () => {
    setSelectedTrend(null);
  };
  
  const handleGeneratePost = (trend) => {
    // Store the trend data in localStorage to use in the generate post page
    localStorage.setItem('trendTopic', JSON.stringify({
      topic: trend.description,
      description: trend.description,
      prompt: `Write a LinkedIn post about ${trend.title}. Include these key points:\n\n${trend.keyTakeaways.join('\n')}\n\nSummary: ${trend.summary}`
    }));
    
    // Navigate to the generate post page
    navigate('/dashboard/generate-post');
  };
  
  const handleLoadSavedSearch = (savedSearch) => {
    setQuery(savedSearch.query);
    setTrends(savedSearch.results);
  };
  
  const handleDeleteSavedSearch = (id, e) => {
    e.stopPropagation(); // Prevent triggering the parent click
    setSavedSearches(prev => prev.filter(item => item.id !== id));
  };
  
  const handleClearAllSearches = () => {
    setSavedSearches([]);
    setTrends(null);
    setQuery('');
  };

  // Conditional rendering based on subscription status
  if (subscriptionStatusLoading) {
    return <LoadingScreen message="Loading Trends & News..." />;
  }

  // Main component rendering logic
  const renderMainContent = () => (
    <>
      <Helmet>
        <title>LinkedIn Trend Finder | Instation</title>
        <meta name="description" content="Discover trending topics and content ideas for your LinkedIn posts." />
      </Helmet>
      
      <Container maxWidth="xl" className="page-container">
        <Box className="content-container">
          <Grid container spacing={3}>
            <Grid item xs={12} className="content-panel">
              <Paper elevation={0} className="content-paper" sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)', minHeight: '750px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                      variant="h6" 
                      className="component-heading"
                      sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                    >
                      AI Trend Finder
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Discover trending topics and insights for your content
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Enter your topic (e.g., design, marketing, technology...)"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#458EFF',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#458EFF',
                          }
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSearch}
                      disabled={loading || !query.trim()}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                      sx={{
                        borderRadius: 2,
                        bgcolor: 'var(--primary-color)',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': {
                          bgcolor: 'var(--primary-dark)'
                        }
                      }}
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                  </Box>
                </Box>

                {/* Saved Searches Section - More compact */}
                {savedSearches.length > 0 && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    mb: 1.5,
                    pb: 1,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 'fit-content' }}>
                        <HistoryIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
                          Recent Searches:
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {savedSearches.map((savedSearch, index) => (
                          <Chip
                            key={index}
                            label={savedSearch.query}
                            onClick={() => handleLoadSavedSearch(savedSearch)}
                            onDelete={(e) => handleDeleteSavedSearch(savedSearch.id, e)}
                            deleteIcon={<CloseIcon fontSize="small" />}
                            size="small"
                            sx={{
                              bgcolor: '#f0f4f8',
                              height: 24,
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.75rem'
                              },
                              '& .MuiChip-deleteIcon': {
                                fontSize: '0.75rem',
                                margin: '0 2px 0 -6px'
                              },
                              '&:hover': {
                                bgcolor: '#e1e8ef'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearAllSearches}
                      sx={{ 
                        color: 'text.secondary', 
                        textTransform: 'none',
                        minWidth: 'auto',
                        p: 0.5,
                        fontSize: '0.75rem'
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                )}

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        alignItems: 'center'
                      }
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {loading && !trends && (
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    my: 4
                  }}>
                    <AnimatedLogo size={100} color="#4890fe" />
                    <Typography variant="h6" sx={{ mt: 3, color: '#2D3748' }}>
                      {getLoadingMessage(loadingTime).message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {getLoadingMessage(loadingTime).subMessage}
                      {loadingTime > 5 && (
                        <Box component="span" sx={{ display: 'inline-block', ml: 0.5 }}>
                          {Array(loadingTime % 4).fill('.').join('')}
                        </Box>
                      )}
                    </Typography>
                    {loadingTime > 0 && (
                      <Box sx={{ mt: 3, width: '200px' }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(loadingTime * 5, 95)} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: 'rgba(72, 144, 254, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#4890fe',
                              borderRadius: 3
                            }
                          }} 
                        />
                      </Box>
                    )}
                  </Box>
                )}
                
                {loading && trends && (
                  <Box sx={{ mb: 3 }}>
                    <LinearProgress sx={{ borderRadius: 1, mb: 2 }} />
                    <Grid container spacing={2}>
                      {[1, 2, 3, 4, 5, 6].map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item}>
                          <Box sx={{ 
                            p: 3,
                            borderRadius: 2,
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            height: '100%',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                              <Skeleton variant="text" width="70%" height={30} />
                            </Box>
                            <Skeleton variant="text" width="90%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="85%" />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                <Box sx={{ 
                  flex: 1,
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '5px',
                    backgroundColor: 'transparent'
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                    borderRadius: '10px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '10px',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.3)'
                    }
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                  mt: 1,
                  mx: -1,
                  px: 1,
                  pb: 2
                }}>
                  {!trends && !loading && !error && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      color: 'text.secondary',
                      p: 3
                    }}>
                      <Box sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        bgcolor: 'rgba(69, 142, 255, 0.1)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mb: 3
                      }}>
                        <TrendingUpIcon sx={{ fontSize: 40, color: '#458EFF' }} />
                      </Box>
                      <Typography variant="h6" sx={{ textAlign: 'center', color: '#2D3748', mb: 1, fontWeight: 600 }}>
                        Discover trending topics
                      </Typography>
                      <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
                        Enter a topic in the search box above to find the latest trends, insights, and content ideas.
                      </Typography>
                    </Box>
                  )}
                  
                  <Fade in={trends !== null && !loading}>
                    <Grid container spacing={2}>
                      {trends?.map((trend, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card 
                            sx={{ 
                              height: '100%',
                              borderRadius: 2,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              cursor: 'pointer',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
                              },
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                            onClick={() => handleOpenTrend(trend)}
                          >
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ 
                                  p: 0.5, 
                                  borderRadius: 1, 
                                  bgcolor: 'rgba(69, 142, 255, 0.1)',
                                  color: '#458EFF',
                                  mr: 1.5,
                                  mt: 0.5
                                }}>
                                  <LightbulbIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Typography fontWeight={600} color="#2D3748" sx={{ lineHeight: 1.3 }}>
                                  {trend.title}
                                </Typography>
                              </Box>
                              
                              <Typography 
                                color="text.secondary" 
                                variant="body2" 
                                sx={{ 
                                  mb: 2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical'
                                }}
                              >
                                {trend.summary}
                              </Typography>
                              
                              <Box sx={{ mt: 'auto' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                  {trend.keyTakeaways.length} key takeaways
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Fade>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Trend Detail Dialog */}
          <Dialog 
            open={selectedTrend !== null} 
            onClose={handleCloseTrend}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {selectedTrend && (
              <>
                <DialogTitle sx={{ 
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LightbulbIcon sx={{ color: '#458EFF', mr: 1.5, fontSize: 24 }} />
                    <Typography variant="h6" fontWeight={600} color="#2D3748">
                      {selectedTrend.title}
                    </Typography>
                  </Box>
                  <IconButton onClick={handleCloseTrend} size="small">
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 3 }}>
                  {/* Summary Section */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <FormatQuoteIcon sx={{ color: '#458EFF', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} color="#2D3748">
                        Summary
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(69, 142, 255, 0.05)'
                    }}>
                      <Typography color="#2D3748">
                        {selectedTrend.summary}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Key Takeaways Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#2D3748" gutterBottom>
                      Key Takeaways:
                    </Typography>
                    <List 
                      dense 
                      sx={{ 
                        bgcolor: 'rgba(0, 0, 0, 0.02)', 
                        borderRadius: 2,
                        p: 2
                      }}
                    >
                      {selectedTrend.keyTakeaways.map((point, idx) => (
                        <ListItem 
                          key={idx}
                          sx={{
                            borderBottom: idx < selectedTrend.keyTakeaways.length - 1 ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
                            py: 1
                          }}
                        >
                          <ListItemText 
                            primary={point} 
                            primaryTypographyProps={{
                              color: '#2D3748'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Description Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#2D3748" gutterBottom>
                      Detailed Analysis:
                    </Typography>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.02)'
                    }}>
                      <Typography color="#2D3748">
                        {selectedTrend.description}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Controversial Angle Section */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarningAmberIcon sx={{ color: '#E53E3E', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} color="#E53E3E">
                        Controversial Angle:
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'rgba(229, 62, 62, 0.05)',
                      border: '1px solid rgba(229, 62, 62, 0.1)'
                    }}>
                      <Typography color="#E53E3E">
                        {selectedTrend.controversialAngle}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Sources Section */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#2D3748" gutterBottom>
                      Sources:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedTrend.citations.map((citation, idx) => (
                        <Chip
                          key={idx}
                          label={`Source ${idx + 1}`}
                          component="a"
                          href={citation}
                          target="_blank"
                          clickable
                          size="small"
                          icon={<LinkIcon fontSize="small" />}
                          sx={{
                            bgcolor: 'rgba(69, 142, 255, 0.08)',
                            color: '#458EFF',
                            fontWeight: 500,
                            '&:hover': {
                              bgcolor: 'rgba(69, 142, 255, 0.15)',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </DialogContent>
                
                <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.05)' }}>
                  <Button 
                    variant="contained"
                    startIcon={<PostAddIcon />}
                    onClick={() => handleGeneratePost(selectedTrend)}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: 'var(--primary-color)',
                      borderRadius: 2,
                      mr: 'auto',
                      '&:hover': {
                        bgcolor: 'var(--primary-dark)'
                      }
                    }}
                  >
                    Generate Post
                  </Button>
                  <Button 
                    onClick={handleCloseTrend}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 600,
                      color: '#4A5568'
                    }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        </Box>
      </Container>
    </>
  );

  // Show overlay first if feature is not allowed
  if (!isFeatureAllowed) {
    // Render minimal skeleton UI covering the viewport
    return (
      <Box sx={{ 
        position: 'relative', 
        height: 'calc(100vh - 64px)', // Adjust 64px based on your header/app bar height
        overflow: 'hidden'
      }}>
        {/* Apply opacity to the skeleton container */}
        <Box sx={{ opacity: 0.4, height: '100%' }}>
          <Container maxWidth="xl" className="page-container">
            <Box className="content-container">
              <Grid container spacing={3}>
                <Grid item xs={12} md={8} className="content-panel">
                  <Paper elevation={0} className="content-paper" sx={{ p: 3, borderRadius: 2, bgcolor: 'transparent', boxShadow: 'none' }}>
                    {/* Skeleton Search Area - Improved */}
                    <Skeleton variant="text" width="30%" height={40} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 3 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Skeleton variant="rectangular" height={56} sx={{ flexGrow: 1, mr: 2, borderRadius: 2 }} />
                      <Skeleton variant="rectangular" width={150} height={56} sx={{ borderRadius: 2 }} />
                    </Box>

                    {/* Skeleton Results Area - Improved */}
                    <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {Array.from(new Array(9)).map((_, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper sx={{ p: 2, borderRadius: 2, height: 180, mb: 2 }}>
                            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} animation="wave" />
                            <Skeleton variant="text" animation="wave" />
                            <Skeleton variant="text" animation="wave" />
                            <Skeleton variant="text" width="80%" animation="wave" />
                            <Box sx={{ mt: 2 }}>
                              <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} animation="wave" />
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4} className="content-panel">
                  <Paper elevation={0} className="content-paper" sx={{ p: 3, borderRadius: 2, bgcolor: 'transparent', boxShadow: 'none' }}>
                    {/* Skeleton History - Improved */}
                     <Skeleton variant="text" width="50%" height={30} sx={{ mb: 2 }} />
                     <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1, mb: 2, ml: 'auto' }} />
                     {Array.from(new Array(4)).map((_, index) => (
                       <Skeleton key={index} variant="rectangular" height={40} sx={{ borderRadius: 1, mb: 1 }} />
                     ))}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
        
        {/* Overlay remains centered on top */}
        <FeatureLockOverlay featureName="AI Trend Finder" currentPlan={currentPlanId} />
      </Box>
    );
  }

  // Handle initial loading of saved data after subscription check
  if (initialLoad && isFeatureAllowed) { // Check isFeatureAllowed here too
    return <LoadingScreen message="Loading Trends & News..." />;
  }

  return renderMainContent();
};

export default TrendFinderPage; 